import type {
  AppData,
  Currency,
  Lot,
  ReturnRecord,
  Sale,
  Settings,
  StockRow,
  Summary,
} from "./types";
import { convert, daysUntil, todayISO } from "./format";

export function lotRemaining(lot: Lot, sales: Sale[], returns: ReturnRecord[]): number {
  let remaining = lot.quantity;
  sales.forEach((sale) => {
    sale.items.forEach((item) => {
      item.consumptions.forEach((c) => {
        if (c.lotId === lot.id) remaining -= c.quantity;
      });
    });
  });
  returns.forEach((r) => {
    if (r.lotId !== lot.id) return;
    if (r.removesInventory) remaining -= r.quantity;
    if (r.restock) remaining += r.quantity;
  });
  return Math.max(0, remaining);
}

export function saleIncome(sale: Sale): number {
  const gross = sale.items.reduce((sum, i) => sum + i.quantity * i.unitPrice, 0);
  return Math.max(0, gross - (sale.discount || 0));
}

export function saleCost(sale: Sale): number {
  return sale.items.reduce((sum, i) => {
    const fromConsumptions = i.consumptions.reduce(
      (s, c) => s + c.quantity * c.unitCost,
      0,
    );
    return sum + (fromConsumptions || i.costOfGoods || 0);
  }, 0);
}

export function purchaseTotal(data: AppData, purchaseId: string): { total: number; units: number } {
  const lots = data.lots.filter((l) => l.purchaseId === purchaseId);
  const total = lots.reduce((s, l) => s + l.quantity * l.unitCost, 0);
  const units = lots.reduce((s, l) => s + l.quantity, 0);
  return { total, units };
}

export function buildStock(data: AppData): StockRow[] {
  const map = new Map<string, StockRow>();
  data.lots.forEach((lot) => {
    const remaining = lotRemaining(lot, data.sales, data.returns);
    let row = map.get(lot.productId);
    if (!row) {
      const product = data.products.find((p) => p.id === lot.productId);
      row = {
        productId: lot.productId,
        productName: lot.productName,
        category: product?.category || "Sin categoría",
        unit: product?.unit || "unidad",
        quantity: 0,
        avgCost: 0,
        inventoryValue: 0,
        bestSalePrice: lot.salePrice,
        potentialRevenue: 0,
        potentialProfit: 0,
        currency: lot.currency,
        nextExpiration: null,
        lots: [],
      };
      map.set(lot.productId, row);
    }
    row.lots.push({ ...lot, quantity: remaining });
    row.quantity += remaining;
    row.inventoryValue += remaining * lot.unitCost;
    if (lot.salePrice > row.bestSalePrice) row.bestSalePrice = lot.salePrice;
    if (lot.expirationDate) {
      if (!row.nextExpiration || lot.expirationDate < row.nextExpiration) {
        row.nextExpiration = lot.expirationDate;
      }
    }
  });
  return Array.from(map.values()).map((row) => ({
    ...row,
    avgCost: row.quantity > 0 ? row.inventoryValue / row.quantity : 0,
    potentialRevenue: row.quantity * row.bestSalePrice,
    potentialProfit: row.potentialRevenue - row.inventoryValue,
  }));
}

function conv(amount: number, from: Currency, to: Currency, s: Settings, storedRate?: number | null) {
  return convert(amount, from, to, s.exchangeRate, storedRate);
}

export function buildSummary(data: AppData): Summary {
  const s = data.settings;
  const cur = s.presentationCurrency;

  const salesIncome = data.sales.reduce(
    (sum, sale) => sum + conv(saleIncome(sale), sale.currency, cur, s, sale.exchangeRate),
    0,
  );
  const cogs = data.sales.reduce(
    (sum, sale) => sum + conv(saleCost(sale), sale.currency, cur, s, sale.exchangeRate),
    0,
  );
  const purchaseSpend = data.lots.reduce(
    (sum, l) => sum + conv(l.quantity * l.unitCost, l.currency, cur, s),
    0,
  );
  const extraExpenses = data.expenses.reduce(
    (sum, e) => sum + conv(e.amount, e.currency, cur, s, e.exchangeRate),
    0,
  );
  const refundsRecovered = data.returns
    .filter((r) => r.reason === "to_supplier" && r.refundReceived)
    .reduce((sum, r) => sum + conv(r.amount, r.currency, cur, s, r.exchangeRate), 0);
  const losses = data.returns
    .filter((r) => ["expired", "damaged", "defective"].includes(r.reason))
    .reduce((sum, r) => sum + conv(r.amount, r.currency, cur, s, r.exchangeRate), 0);
  const inventoryValue = data.lots.reduce(
    (sum, l) => sum + conv(lotRemaining(l, data.sales, data.returns) * l.unitCost, l.currency, cur, s),
    0,
  );

  const grossProfit = salesIncome - cogs;
  const netProfit = grossProfit - extraExpenses - losses;

  return {
    salesIncome,
    purchaseSpend,
    grossProfit,
    extraExpenses,
    refundsRecovered,
    inventoryValue,
    netProfit,
    currency: cur,
  };
}

export interface ExpiringItem {
  lot: Lot;
  remaining: number;
  days: number;
}

export function buildExpiring(data: AppData, withinDays = 30): ExpiringItem[] {
  const items: ExpiringItem[] = [];
  data.lots.forEach((lot) => {
    if (!lot.expirationDate) return;
    const remaining = lotRemaining(lot, data.sales, data.returns);
    if (remaining <= 0) return;
    const days = daysUntil(lot.expirationDate);
    if (days === null) return;
    if (days <= withinDays) items.push({ lot, remaining, days });
  });
  return items.sort((a, b) => a.days - b.days);
}

export interface TimelineEntry {
  id: string;
  kind: "purchase" | "sale" | "expense" | "return";
  date: string;
  title: string;
  subtitle: string;
  amount: number;
  currency: Currency;
  positive: boolean;
}

export function buildTimeline(data: AppData): TimelineEntry[] {
  const entries: TimelineEntry[] = [];

  data.purchases.forEach((p) => {
    const { total, units } = purchaseTotal(data, p.id);
    entries.push({
      id: p.id,
      kind: "purchase",
      date: p.date,
      title: "Compra de inventario",
      subtitle: `${units} unidades${p.supplier ? ` · ${p.supplier}` : ""}`,
      amount: total,
      currency: p.currency,
      positive: false,
    });
  });

  data.sales.forEach((sale) => {
    entries.push({
      id: sale.id,
      kind: "sale",
      date: sale.date,
      title: "Venta",
      subtitle: `${sale.items.length} producto(s)${sale.customer ? ` · ${sale.customer}` : ""}`,
      amount: saleIncome(sale),
      currency: sale.currency,
      positive: true,
    });
  });

  data.expenses.forEach((e) => {
    entries.push({
      id: e.id,
      kind: "expense",
      date: e.date,
      title: "Gasto adicional",
      subtitle: e.category,
      amount: e.amount,
      currency: e.currency,
      positive: false,
    });
  });

  data.returns.forEach((r) => {
    entries.push({
      id: r.id,
      kind: "return",
      date: r.date,
      title: "Devolución / pérdida",
      subtitle: r.productName,
      amount: r.amount,
      currency: r.currency,
      positive: r.reason === "to_supplier" && r.refundReceived,
    });
  });

  return entries.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
}

export function isExpiredSoon(date: string | null, withinDays = 30): boolean {
  const d = daysUntil(date);
  return d !== null && d <= withinDays;
}

export function freshToday(): string {
  return todayISO();
}